import React from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon, ShieldCheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const RiskScoreCard = ({ riskScore, explanation, detailed = false, concentrationMetrics = {} }) => {
  const getRiskLevel = (score) => {
    if (score <= 3) return { level: 'Low', color: 'text-green-400', bgColor: 'bg-green-400/10', icon: ShieldCheckIcon };
    if (score <= 6) return { level: 'Moderate', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10', icon: ExclamationTriangleIcon };
    return { level: 'High', color: 'text-red-400', bgColor: 'bg-red-400/10', icon: ExclamationTriangleIcon };
  };

  const riskLevel = getRiskLevel(riskScore);
  const Icon = riskLevel.icon;

  const getRiskColor = (score) => {
    if (score <= 3) return '#10B981'; // Green
    if (score <= 6) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const RiskGauge = () => (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
        {/* Background circle */}
        <path
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="#374151"
          strokeWidth="2"
        />
        {/* Progress circle */}
        <path
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke={getRiskColor(riskScore)}
          strokeWidth="2"
          strokeDasharray={`${(riskScore / 10) * 100}, 100`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className={`text-2xl font-bold ${riskLevel.color}`}>
            {riskScore}
          </div>
          <div className="text-xs text-gray-400">/ 10</div>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gray-800 rounded-lg p-6 ${detailed ? 'col-span-full' : ''}`}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Icon className="h-5 w-5 text-aurora-blue" />
          Risk Analysis
        </h2>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${riskLevel.bgColor} ${riskLevel.color}`}>
          {riskLevel.level} Risk
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Gauge */}
        <div className="text-center">
          <RiskGauge />
          <p className="text-gray-400 text-sm mt-4">
            Portfolio Risk Score
          </p>
        </div>

        {/* Risk Explanation */}
        <div className="space-y-4">
          <div>
            <h3 className="text-white font-medium mb-2">AI Analysis</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {explanation}
            </p>
          </div>

          {/* Risk Gym Link */}
          <div className="bg-gray-700 rounded-lg p-4">
            <p className="text-white text-sm font-medium mb-2">
              Want to explore this risk in different market conditions?
            </p>
            <button className="text-aurora-blue hover:text-aurora-blue-dark text-sm font-medium transition-colors">
              Try a simulation in the Risk Gym →
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      {detailed && concentrationMetrics && Object.keys(concentrationMetrics).length > 0 && (
        <div className="mt-8 border-t border-gray-700 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Risk Factors</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Top 5 Holdings</span>
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-white">
                {concentrationMetrics.top_5_percentage?.toFixed(1) || 0}%
              </div>
              <div className="text-xs text-gray-400">
                {concentrationMetrics.top_5_percentage > 60 ? 'High concentration' : 
                 concentrationMetrics.top_5_percentage > 40 ? 'Moderate concentration' : 'Well diversified'}
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Sectors</span>
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-white">
                {concentrationMetrics.num_sectors || 0}
              </div>
              <div className="text-xs text-gray-400">
                {concentrationMetrics.num_sectors > 8 ? 'Well diversified' :
                 concentrationMetrics.num_sectors > 4 ? 'Moderately diversified' : 'Limited diversification'}
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Largest Sector</span>
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </div>
              <div className="text-lg font-bold text-white">
                {concentrationMetrics.largest_sector || 'N/A'}
              </div>
              <div className="text-xs text-gray-400">
                {concentrationMetrics.largest_sector_percentage?.toFixed(1) || 0}% of portfolio
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Holdings</span>
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-white">
                {concentrationMetrics.num_holdings || 0}
              </div>
              <div className="text-xs text-gray-400">
                {concentrationMetrics.num_holdings > 15 ? 'Well diversified' :
                 concentrationMetrics.num_holdings > 8 ? 'Moderately diversified' : 'Concentrated'}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RiskScoreCard;
